import {
  IPinnacleAffectedPart,
  IPinnacleItemType,
  IPinnacleMatchSheetRow,
  IPinnacleUsage,
  PinnacleConfidence,
  PinnacleItemTypeStatus,
  PinnaclePartStatus,
  PinnaclePosition,
  PinnacleSide,
} from "../../models/PinnacleMapping/PinnacleMapping";

export const sideLabel: Record<PinnacleSide, string> = { Left: "LH / N/S", Right: "RH / O/S" };

/** "[DOOR_MIRROR]" becomes "Door Mirror", as a starting name for a new category. */
export function prettyItemTypeName(name: string) {
  return name
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** "LH + Front + "interior"", or "Any part" for a default mapping. */
export function describeConditions(c: { side: PinnacleSide | null; position: PinnaclePosition | null; keyword: string | null }) {
  const parts = [
    c.side === "Left" ? "LH" : c.side === "Right" ? "RH" : null,
    c.position,
    c.keyword ? `"${c.keyword}"` : null,
  ].filter(Boolean);

  return parts.length ? parts.join(" + ") : "Any part";
}

export const itemStatusMeta: Record<PinnacleItemTypeStatus, { label: string; className: string }> = {
  Unmapped: { label: "Unmapped", className: "bg-red-100 text-red-700 ring-red-600/20" },
  NeedsCheck: { label: "Check legacy", className: "bg-amber-100 text-amber-800 ring-amber-600/20" },
  Mapped: { label: "Mapped", className: "bg-green-100 text-green-700 ring-green-600/20" },
  Ignored: { label: "Ignored", className: "bg-gray-100 text-gray-600 ring-gray-500/20" },
};

export const confidenceMeta: Record<PinnacleConfidence, string> = {
  High: "bg-green-100 text-green-700 ring-green-600/20",
  Medium: "bg-amber-100 text-amber-800 ring-amber-600/20",
  Low: "bg-gray-100 text-gray-600 ring-gray-500/20",
};

export const partStatusMeta: Record<PinnaclePartStatus, { label: string; className: string }> = {
  Move: { label: "Move", className: "bg-blue-100 text-blue-700 ring-blue-600/20" },
  Unchanged: { label: "Unchanged", className: "bg-gray-100 text-gray-600 ring-gray-500/20" },
  Listed: { label: "Listed on eBay", className: "bg-amber-100 text-amber-800 ring-amber-600/20" },
  Accepted: { label: "Accepted", className: "bg-gray-100 text-gray-600 ring-gray-500/20" },
  NeedsCategory: { label: "Needs a category", className: "bg-red-100 text-red-700 ring-red-600/20" },
  NoMatch: { label: "No mapping fits", className: "bg-gray-100 text-gray-600 ring-gray-500/20" },
};

/**
 * Where a part would go and what that means, once someone may have picked a category for it by
 * hand. Mirrors Core's PinnacleCategoryMappingService.StatusFor, with the override taking the place
 * of the mapping's answer. An accepted part only counts as accepted while nobody has overridden it:
 * picking a category for it is the decision to move it.
 */
export function partOutcome(part: IPinnacleAffectedPart, override?: string): { target: string | null; status: PinnaclePartStatus } {
  const target = override ?? part.proposedCategoryId;

  if (!target) return { target: null, status: part.currentCategoryId ? "NoMatch" : "NeedsCategory" };
  if (target === part.currentCategoryId) return { target, status: "Unchanged" };
  if (part.accepted && !override) return { target, status: "Accepted" };
  if (part.listed) return { target, status: "Listed" };

  return { target, status: "Move" };
}

/** Outcomes a part can be ticked for. Only "Move" is ticked to begin with. */
export const tickable: PinnaclePartStatus[] = ["Move", "Listed", "Accepted"];

export type ItemTypeFilter = "all" | "todo" | "Unmapped" | "NeedsCheck" | "Mapped" | "Ignored";

export interface ItemTypeRow {
  itemType: IPinnacleItemType;
  usage?: IPinnacleUsage;
}

/**
 * What needs doing first: item types with parts waiting and no mapping, then unchecked legacy
 * mappings, then the rest. Within each, the most waiting parts first.
 */
export function sortItemTypes(rows: ItemTypeRow[]) {
  const rank: Record<PinnacleItemTypeStatus, number> = { Unmapped: 0, NeedsCheck: 1, Mapped: 2, Ignored: 3 };

  return [...rows].sort(
    (a, b) =>
      rank[a.itemType.status] - rank[b.itemType.status] ||
      (b.usage?.uncategorised ?? 0) - (a.usage?.uncategorised ?? 0) ||
      (b.usage?.parts ?? 0) - (a.usage?.parts ?? 0) ||
      a.itemType.name.localeCompare(b.itemType.name),
  );
}

export function filterItemTypes(rows: ItemTypeRow[], filter: ItemTypeFilter, search: string) {
  const term = search.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

  return rows.filter((row) => {
    if (term && !row.itemType.key.includes(term)) return false;
    if (filter === "all") return row.itemType.status !== "Ignored";
    if (filter === "todo") return row.itemType.status === "Unmapped" || row.itemType.status === "NeedsCheck" || (row.usage?.uncategorised ?? 0) > 0;
    return row.itemType.status === filter;
  });
}

/**
 * Reads the Pinnacle match sheet (partcategory_pinnacle_matches.csv): a header row, then quoted
 * values with columns Id, Name, itemtype_id, pinnacle_name, normalised_key, confidence. Columns are
 * found by header name so their order does not matter.
 */
export function parseMatchSheet(text: string): IPinnacleMatchSheetRow[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length < 2) return [];

  const header = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  const col = (name: string) => header.indexOf(name);
  const [id, pinnacle, itemTypeId, confidence] = [col("id"), col("pinnacle_name"), col("itemtype_id"), col("confidence")];

  if (id < 0 || pinnacle < 0) return [];

  return lines.slice(1).flatMap((line) => {
    const cells = splitCsvLine(line);
    const pinnacleName = cells[pinnacle]?.trim();
    if (!pinnacleName) return [];

    const parsedId = itemTypeId >= 0 ? parseInt(cells[itemTypeId], 10) : NaN;

    return [
      {
        categoryId: cells[id]?.trim() ?? "",
        pinnacleName,
        itemTypeId: Number.isFinite(parsedId) ? parsedId : null,
        confidence: confidence >= 0 ? cells[confidence]?.trim() ?? "low" : "low",
      },
    ];
  });
}

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let quoted = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }

  cells.push(current);
  return cells;
}
